import { Alert, BrandLogo, Button, Card, Field } from '@catering/ui';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { type FormEvent, type JSX, useState } from 'react';

import { ApiError } from '../api/client';
import { acceptInvitation } from './invitation-api';

/**
 * `design.md` §5.2 — copied from `apps/web-portal/src/auth/accept-invitation-page.tsx`
 * with two differences: staff-oriented copy and a redirect to `web-admin`'s
 * own `/login` instead of the portal's. No branching by `isInternal` here:
 * `EmailInvitationNotifier` already picked which base URL the email link
 * uses, so this page only ever serves the base it was reached on.
 */
export function AcceptInvitationPage(): JSX.Element {
  const navigate = useNavigate();
  const { token } = useSearch({ from: '/accept-invitation' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    if (token === undefined) {
      return;
    }

    const form = new FormData(event.currentTarget);
    const password = readText(form, 'password');

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await acceptInvitation(token, password);

      await navigate({ to: '/login', search: { activated: 'success' } });
    } catch (error) {
      setErrorMessage(
        error instanceof ApiError ? describeAcceptError(error) : TRY_AGAIN,
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 via-white to-slate-50 p-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-1">
          <BrandLogo size="lg" />
          <span className="text-sm text-slate-500">Back-office</span>
        </div>

        <Card variant="elevated" className="w-full">
          <form
            onSubmit={(event) => void handleSubmit(event)}
            className="flex flex-col gap-5"
            noValidate
          >
            <header>
              <h1 className="text-lg font-semibold text-slate-900">Activá tu cuenta interna</h1>
              <p className="mt-1 text-sm text-slate-500">
                Elegí una contraseña para activar tu acceso al back-office.
              </p>
            </header>

            {token === undefined ? (
              <Alert tone="danger">
                Este enlace no incluye una invitación válida. Pedile a quien te invitó que te envíe
                una nueva.
              </Alert>
            ) : null}

            {errorMessage !== null ? <Alert tone="danger">{errorMessage}</Alert> : null}

            <Field
              label="Contraseña"
              name="password"
              type="password"
              autoComplete="new-password"
              hint="Entre 10 y 128 caracteres"
              required
              autoFocus
              disabled={token === undefined}
            />

            <Button
              type="submit"
              loading={isSubmitting}
              disabled={token === undefined}
              className="w-full"
            >
              Activar cuenta
            </Button>
          </form>
        </Card>
      </div>
    </main>
  );
}

function readText(form: FormData, field: string): string {
  const value = form.get(field);

  return typeof value === 'string' ? value : '';
}

const TRY_AGAIN = 'No pudimos activar tu cuenta. Intentá de nuevo en un momento.';

const INVALID_INVITATION =
  'Invitación inválida o vencida. Pedile a quien te invitó que te envíe una nueva.';

/**
 * A rejected password is not a broken invitation: saying so would send the
 * person to ask for a new link when all they need is another password.
 */
function describeAcceptError(error: ApiError): string {
  const body = error.body as { violations?: unknown } | null;
  const violations = Array.isArray(body?.violations) ? (body.violations as string[]) : [];

  if (violations.includes('BREACHED')) {
    return 'Esa contraseña apareció en filtraciones de datos conocidas. Elegí otra.';
  }
  if (violations.includes('TOO_SHORT')) {
    return 'La contraseña tiene que tener al menos 10 caracteres.';
  }
  if (violations.includes('TOO_LONG')) {
    return 'La contraseña puede tener hasta 128 caracteres.';
  }

  if (error.status === 401) {
    return INVALID_INVITATION;
  }

  return TRY_AGAIN;
}
